import React from 'react';
import { ActionIcon, Badge, Box, Card, Group, Text, Tooltip } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import type { RequestTrackingItem, RequestTrackingStatus } from '../logic/types';

function statusColor(status: RequestTrackingStatus): string {
  switch (status) {
    case 'queued':
      return 'gray';
    case 'running':
      return 'blue';
    case 'completed':
      return 'teal';
    case 'failed':
      return 'red';
    case 'cancelled':
      return 'yellow';
    default:
      return 'dark';
  }
}

function formatRequestTypeLabel(requestType: string): string {
  const t = requestType.trim().toUpperCase();
  if (t === 'PROJECT_BASELINE_CREATE' || t.includes('BASELINE')) {
    return 'Baseline';
  }
  if (t === 'MS_PROJECT_WBS_SETUP' || t.includes('WBS')) {
    return 'WBS import';
  }
  return requestType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseRequestDate(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatRequestTimestamp(value: string | null | undefined): string {
  const parsed = parseRequestDate(value);
  if (!parsed) return value?.trim() ? value : '—';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) {
    const sec = totalSec % 60;
    return sec > 0 ? `${totalMin}m ${sec}s` : `${totalMin}m`;
  }
  const totalHr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (totalHr < 24) {
    return min > 0 ? `${totalHr}h ${min}m` : `${totalHr}h`;
  }
  const days = Math.floor(totalHr / 24);
  const hr = totalHr % 24;
  return hr > 0 ? `${days}d ${hr}h` : `${days}d`;
}

/** Elapsed time from request start to completion (or now while still running). */
function formatRequestDuration(
  requestTime: string | null | undefined,
  requestCompletedTime: string | null | undefined,
  inProgress: boolean,
): string | null {
  const start = parseRequestDate(requestTime);
  if (!start) return null;
  const end = inProgress ? new Date() : parseRequestDate(requestCompletedTime);
  if (!end) return null;
  return formatElapsed(Math.max(0, end.getTime() - start.getTime()));
}

function MetaSep() {
  return (
    <Text span size="xs" c="dimmed" mx={4}>
      ·
    </Text>
  );
}

interface RequestTrackingProgressBarProps {
  requestId: string;
  targetPercent: number | null;
  isActive: boolean;
  isRefreshing: boolean;
}

function RequestTrackingProgressBar({
  requestId,
  targetPercent,
  isActive,
  isRefreshing,
}: RequestTrackingProgressBarProps) {
  const [displayPercent, setDisplayPercent] = React.useState(targetPercent ?? 0);

  React.useEffect(() => {
    if (targetPercent == null) return;
    setDisplayPercent(targetPercent);
  }, [requestId, targetPercent]);

  const showBar = targetPercent != null || isActive;
  if (!showBar) return null;

  const clamped = Math.max(0, Math.min(100, displayPercent));
  const showShimmer = isActive && clamped < 100;
  const showPollingPulse = isRefreshing && isActive;

  return (
    <Group gap={8} wrap="nowrap" align="center" mt={6}>
      <Box
        style={{ flex: 1, minWidth: 0 }}
        className={[
          'request-tracking-progress-track',
          showPollingPulse ? 'request-tracking-progress-track--polling' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Box
          className={[
            'request-tracking-progress-fill',
            showShimmer ? 'request-tracking-progress-fill--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            width: `${targetPercent != null ? clamped : 8}%`,
            minWidth: targetPercent != null && clamped > 0 ? 4 : 0,
          }}
        />
      </Box>
      <Text
        size="xs"
        fw={600}
        w={36}
        ta="right"
        className={showPollingPulse ? 'request-tracking-progress-label--polling' : undefined}
      >
        {targetPercent != null ? `${Math.round(clamped)}%` : '—'}
      </Text>
    </Group>
  );
}

export interface RequestTrackingItemCardProps {
  item: RequestTrackingItem;
  isRefreshing?: boolean;
  onCancel?: (requestId: string) => void;
}

export function RequestTrackingItemCard({
  item,
  isRefreshing = false,
  onCancel,
}: RequestTrackingItemCardProps) {
  const canCancel = item.status === 'queued' || item.status === 'running';
  const isActive = item.status === 'queued' || item.status === 'running';
  const progressPercent =
    item.progress != null ? Math.max(0, Math.min(100, item.progress)) : null;
  const completedLabel = item.requestCompletedTime
    ? formatRequestTimestamp(item.requestCompletedTime)
    : isActive
      ? 'In progress…'
      : '—';
  const durationLabel = formatRequestDuration(
    item.requestTime,
    item.requestCompletedTime,
    isActive,
  );

  return (
    <Card withBorder radius="sm" padding="xs">
      <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
        <Text fw={600} size="xs" lineClamp={1} title={item.fileName} style={{ flex: 1, minWidth: 0 }}>
          {item.fileName}
        </Text>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {item.requestType ? (
            <Tooltip label={item.requestType}>
              <Badge size="xs" variant="outline" color="gray">
                {formatRequestTypeLabel(item.requestType)}
              </Badge>
            </Tooltip>
          ) : null}
          <Badge size="xs" color={statusColor(item.status)} variant="light">
            {item.status}
          </Badge>
          {canCancel && onCancel ? (
            <Tooltip label="Cancel request">
              <ActionIcon
                size="sm"
                variant="subtle"
                color="red"
                aria-label="Cancel request"
                onClick={() => onCancel(item.requestId)}
              >
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      </Group>

      <Text size="xs" c="dimmed" lineClamp={2} mt={4} style={{ lineHeight: 1.35 }}>
        {item.userName ? (
          <>
            <Text span size="xs" c="dimmed">
              User{' '}
            </Text>
            <Text span size="xs" fw={500} c="var(--mantine-color-text)">
              {item.userName}
            </Text>
            <MetaSep />
          </>
        ) : null}
        <Text span size="xs" c="dimmed">
          Started{' '}
        </Text>
        <Text span size="xs" c="var(--mantine-color-text)">
          {formatRequestTimestamp(item.requestTime)}
        </Text>
        <MetaSep />
        <Text span size="xs" c="dimmed">
          Done{' '}
        </Text>
        <Text span size="xs" c="var(--mantine-color-text)">
          {completedLabel}
        </Text>
        {durationLabel ? (
          <>
            <MetaSep />
            <Text span size="xs" c="dimmed">
              Duration{' '}
            </Text>
            <Text span size="xs" fw={500} c="var(--mantine-color-text)">
              {durationLabel}
            </Text>
          </>
        ) : null}
      </Text>

      <Tooltip label={item.requestId}>
        <Text size="xs" c="dimmed" ff="monospace" lineClamp={1} mt={4} title={item.requestId}>
          {item.requestId}
        </Text>
      </Tooltip>

      <RequestTrackingProgressBar
        requestId={item.requestId}
        targetPercent={progressPercent}
        isActive={isActive}
        isRefreshing={isRefreshing}
      />

      {item.message ? (
        <Text size="xs" mt={4} c="dimmed" lineClamp={2} title={item.message}>
          {item.message}
        </Text>
      ) : null}
    </Card>
  );
}
